import React, { JSX } from 'react';

export type InfoModalTypes = 'title' | 'full' | 'tooltip';

interface State {
  type: InfoModalTypes;
  visible: boolean;
  icon?: any;
  message?: string;
  title: string;
  subtitle?: string;
  subtitle2?: string;
  showDoneButton?: boolean;
  buttonLabel?: string;
  buttonColor?: string;
  showClose?: boolean;
  closeOnTapOutside?: boolean;
  onButtonPress?: () => void;
  accessoryView?: React.JSX.Element;
}

export interface SetModalAction {
  type: 'SET_MODAL';
  setType: InfoModalTypes;
  setVisible?: boolean;
  setMessage?: string;
  setIcon?: any;
  setTitle: string;
  setSubtitle?: string;
  setSubtitle2?: string;
  setShowDoneButton?: boolean;
  setButtonLabel?: string;
  setButtonColor?: string;
  setShowClose?: boolean;
  setCloseOnTapOutside?: boolean;
  setOnButtonPress?: () => void;
  setAccessoryView?: React.JSX.Element;
}

export interface SetVisibleAction {
  type: 'SET_VISIBLE';
  setVisible: boolean;
}

type Action = SetModalAction | SetVisibleAction;

type Dispatch = (action: Action) => void;

interface InfoModalContextData {
  state: State;
  dispatch: Dispatch;
  showInfo: ({
    type,
    icon,
    title,
    subtitle,
    subtitle2,
    message,
    showDoneButton,
    buttonLabel,
    buttonColor,
    showClose,
    closeOnTapOutside,
    onButtonPress,
    accessoryView
  }: {
    type: InfoModalTypes;
    icon?: any;
    title: string;
    subtitle?: string;
    subtitle2?: string;
    message?: string;
    showDoneButton?: boolean;
    buttonLabel?: string;
    buttonColor?: string;
    showClose?: boolean;
    closeOnTapOutside?: boolean;
    onButtonPress?: () => void;
    accessoryView?: React.JSX.Element;
  }) => void;
}

const InfoModalContext = React.createContext<InfoModalContextData | undefined>(undefined);

function infoModalReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_MODAL':
      return {
        ...state,
        type: action.setType,
        icon: action.setIcon,
        visible: action.setVisible ? action.setVisible : state.visible,
        title: action.setTitle,
        subtitle: action.setSubtitle,
        subtitle2: action.setSubtitle2,
        message: action.setMessage,
        showDoneButton: action.setShowDoneButton,
        buttonLabel: action.setButtonLabel,
        buttonColor: action.setButtonColor,
        showClose: action.setShowClose,
        closeOnTapOutside: action.setCloseOnTapOutside,
        onButtonPress: action.setOnButtonPress,
        accessoryView: action.setAccessoryView
      };
    case 'SET_VISIBLE':
      return {
        ...state,
        visible: action.setVisible
      };
    default:
      throw new Error(`Unhandled action type`);
  }
}

function InfoModalProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, dispatch] = React.useReducer(infoModalReducer, {
    type: 'title',
    visible: false,
    icon: undefined,
    title: '',
    subtitle: undefined,
    subtitle2: undefined,
    message: undefined,
    showDoneButton: undefined,
    buttonLabel: undefined,
    buttonColor: undefined,
    showClose: undefined,
    closeOnTapOutside: undefined,
    onButtonPress: undefined,
    accessoryView: undefined
  });

  const showInfo = ({
    type,
    icon,
    title,
    subtitle,
    subtitle2,
    message,
    showDoneButton,
    buttonLabel,
    buttonColor,
    showClose,
    closeOnTapOutside,
    onButtonPress,
    accessoryView
  }: {
    type: InfoModalTypes;
    icon?: any;
    title: string;
    subtitle?: string;
    subtitle2?: string;
    message?: string;
    showDoneButton?: boolean;
    buttonLabel?: string;
    buttonColor?: string;
    showClose?: boolean;
    closeOnTapOutside?: boolean;
    onButtonPress?: () => void;
    accessoryView?: React.JSX.Element;
  }) => {
    dispatch({
      type: 'SET_MODAL',
      setType: type,
      setIcon: icon,
      setVisible: true,
      setTitle: title,
      setSubtitle: subtitle,
      setSubtitle2: subtitle2,
      setMessage: message,
      setShowDoneButton: showDoneButton,
      setButtonLabel: buttonLabel,
      setButtonColor: buttonColor,
      setShowClose: showClose,
      setCloseOnTapOutside: closeOnTapOutside,
      setOnButtonPress: onButtonPress,
      setAccessoryView: accessoryView
    });
  };

  return <InfoModalContext.Provider value={{ state, dispatch, showInfo }}>{children}</InfoModalContext.Provider>;
}

function useInfoModal(): InfoModalContextData {
  const context = React.useContext(InfoModalContext);
  if (context === undefined) {
    throw new Error('useInfoModal must be used within an InfoModalProvider');
  }
  return context;
}

export { InfoModalProvider, useInfoModal };
